// @ts-nocheck
/*
 * Port of session-replay/replay.js (528 lines). BYTE-IDENTICAL.
 * The player itself:
 *   1. Kill any active WebRTC softphone before we mount (softphone toolbar
 *      is a floating overlay that eats clicks otherwise) â€” v1 clicks each
 *      `.disconnect2` button.
 *   2. Fetch detail + events + chunk metadata in parallel, then stream
 *      chunks in batches of CHUNK_FETCH_BATCH, decode + merge into a
 *      sorted rrweb event array, and progressively reveal the player as
 *      soon as â‰¥2 events land.
 *   3. `Replayer` is torn down + rebuilt when the chunk buffer grows so
 *      the rrweb replayer picks up newly-loaded events; playhead is
 *      preserved via `getCurrentTime()`.
 *   4. Heatmap overlay is fetched per playbackScreen (memoised) when the
 *      user toggles it on â€” cached in a Map so re-visits don't refetch.
 *   5. Right-panel tabs (Timeline / Journey / Insights) are COMMENTED OUT
 *      in v1 â€” kept commented here to match the shipped v1 UI exactly.
 * `space` bar toggles play/pause when focus is not in an input/textarea.
 * `applyScale` fits the recorded viewport (e.g. 1440x900) into the current
 * canvas container using CSS transform: scale.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { ArrowLeft, Film, AlertTriangle } from 'lucide-react';
import { Replayer } from 'rrweb';
import 'rrweb/dist/style.css';
import { getApiCall } from '@/service/httpCall';
import {
  API_SESSION_REPLAY_DETAIL,
  API_SESSION_REPLAY_CHUNKS_META,
  API_SESSION_REPLAY_CHUNKS,
  API_SESSION_REPLAY_EVENTS,
  API_SESSION_REPLAY_ANALYTICS_HEATMAP,
} from '@/service/url';
import Loader from '@/components/partials/components/Loader';
import SessionSummaryHeader from './components/SessionSummaryHeader';
import TimelineScrubber from './components/TimelineScrubber';
import SemanticTimeline from './components/SemanticTimeline';
import JourneyView from './components/JourneyView';
import PlaybackControls from './components/PlaybackControls';
import HeatmapOverlay from './components/HeatmapOverlay';
import {
  decodeChunk,
  deriveJourney,
  computeIdleRanges,
  deriveSessionInsights,
  markerColor,
} from './replayUtils';

const CHUNK_FETCH_BATCH = 5;

function SessionReplayPlayer() {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<any>(null);
  const [bufferedFraction, setBufferedFraction] = useState(0);
  const [allLoaded, setAllLoaded] = useState(false);

  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [skipIdle, setSkipIdle] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);
  const [rightTab, setRightTab] = useState('timeline');

  const canvasRef = useRef<HTMLDivElement | null>(null);
  const replayerRef = useRef<any>(null);
  const rrwebEventsRef = useRef<any[]>([]);
  const playingRef = useRef(false);
  const speedRef = useRef(1);
  const skipIdleRef = useRef(false);
  const aliveRef = useRef(true);

  playingRef.current = playing;
  speedRef.current = speed;
  skipIdleRef.current = skipIdle;

  useEffect(() => {
    try {
      const disconnectBtns = document.getElementsByClassName('disconnect2');
      for (const btn of disconnectBtns) (btn as HTMLElement).click();
      sessionStorage.setItem('showWebrtc', 'NO');
      sessionStorage.removeItem('webrtcPhoneStatus');
    } catch (e) {
      /* noop â€” teardown is best-effort */
    }
  }, []);

  const durationMs = useMemo(() => {
    if (session?.duration_seconds) return session.duration_seconds * 1000;
    const evs = rrwebEventsRef.current;
    if (evs.length > 1) return evs[evs.length - 1].timestamp - evs[0].timestamp;
    return 0;
  }, [session, allLoaded]);

  const journey = useMemo(
    () => deriveJourney(events, session?.ended_at),
    [events, session],
  );
  const idleRanges = useMemo(() => computeIdleRanges(events), [events]);
  const sessionInsights = useMemo(
    () => deriveSessionInsights(session, events, journey),
    [session, events, journey],
  );

  // ---- heatmap overlay ----
  const [heatmapOn, setHeatmapOn] = useState(false);
  const [heatmap, setHeatmap] = useState<any>({ screen: null, cells: [], max: 0 });
  const heatmapCacheRef = useRef<Map<string, any>>(new Map());

  // Screen visible at the current playback position.
  const playbackScreen = useMemo(() => {
    if (!session?.started_at) return null;
    const startMs = new Date(session.started_at).getTime();
    let screen = null;
    for (const e of events) {
      if (e.event_type !== 'screen_change') continue;
      if (new Date(e.ts).getTime() - startMs <= currentMs) screen = e.screen;
      else break;
    }
    return screen || journey[0]?.screen || null;
  }, [events, session, currentMs, journey]);

  useEffect(() => {
    if (!heatmapOn || !playbackScreen) return;
    const cached = heatmapCacheRef.current.get(playbackScreen);
    if (cached) {
      setHeatmap(cached);
      return;
    }
    (async () => {
      try {
        const resp: any = await getApiCall(
          `${API_SESSION_REPLAY_ANALYTICS_HEATMAP}?screen=${encodeURIComponent(playbackScreen)}`,
          {},
        );
        const data = {
          screen: playbackScreen,
          cells: Array.isArray(resp?.cells) ? resp.cells : [],
          max: Number(resp?.max_clicks) || 0,
        };
        heatmapCacheRef.current.set(playbackScreen, data);
        setHeatmap(data);
      } catch {
        setHeatmap({ screen: playbackScreen, cells: [], max: 0 });
      }
    })();
  }, [heatmapOn, playbackScreen]);

  // Fit the recorded viewport into the canvas container.
  const applyScale = useCallback(() => {
    const replayer = replayerRef.current;
    const container = canvasRef.current;
    if (!replayer || !container) return;
    try {
      const wrapper = replayer.wrapper;
      const iframe = wrapper?.querySelector('iframe');
      const vw =
        Number(iframe?.getAttribute('width')) || session?.device?.viewport_w || 1280;
      const vh =
        Number(iframe?.getAttribute('height')) || session?.device?.viewport_h || 720;
      const scale = Math.min(
        container.clientWidth / vw,
        container.clientHeight / vh,
        1,
      );
      wrapper.style.transform = `scale(${scale})`;
      wrapper.style.transformOrigin = 'top left';
    } catch {
      /* scaling is cosmetic */
    }
  }, [session]);

  const destroyReplayer = useCallback(() => {
    const replayer = replayerRef.current;
    if (replayer) {
      try {
        replayer.pause();
      } catch {
        /* ignore */
      }
      try {
        replayer.destroy?.();
      } catch {
        /* ignore */
      }
      replayerRef.current = null;
    }
    if (canvasRef.current) canvasRef.current.innerHTML = '';
  }, []);

  const buildReplayer = useCallback(
    (startAtMs = 0, autoPlay = false) => {
      const evs = rrwebEventsRef.current;
      if (!canvasRef.current || evs.length < 2) return;
      destroyReplayer();
      try {
        const replayer: any = new Replayer(evs, {
          root: canvasRef.current,
          skipInactive: skipIdleRef.current,
          speed: speedRef.current,
          mouseTail: { strokeStyle: '#4f46e5', lineWidth: 2, duration: 600 },
        });
        replayerRef.current = replayer;
        replayer.on('finish', () => setPlaying(false));
        if (autoPlay) {
          replayer.play(startAtMs);
          setPlaying(true);
        } else {
          replayer.pause(startAtMs);
        }
        setTimeout(applyScale, 50);
      } catch (err) {
        setLoadError('Could not initialise the replay player for this session.');
      }
    },
    [applyScale, destroyReplayer],
  );

  // ---- data loading ----
  useEffect(() => {
    aliveRef.current = true;
    (async () => {
      setLoading(true);
      try {
        const [detailResp, eventsResp, metaResp]: any = await Promise.all([
          getApiCall(API_SESSION_REPLAY_DETAIL(sessionId), {}),
          getApiCall(API_SESSION_REPLAY_EVENTS(sessionId), {}),
          getApiCall(API_SESSION_REPLAY_CHUNKS_META(sessionId), {}),
        ]);
        if (!aliveRef.current) return;

        setSession(detailResp?.session || null);
        setEvents(Array.isArray(eventsResp?.events) ? eventsResp.events : []);

        const meta = Array.isArray(metaResp?.chunks) ? metaResp.chunks : [];
        if (!meta.length) {
          setLoadError(
            'No replay data stored for this session (chunks may have expired).',
          );
          setLoading(false);
          return;
        }

        const indexes = meta.map((m: any) => Number(m.chunk_index)).sort((a: number, b: number) => a - b);
        const maxIndex = indexes[indexes.length - 1];
        let loadedCount = 0;

        for (let from = indexes[0]; from <= maxIndex; from += CHUNK_FETCH_BATCH) {
          const to = Math.min(maxIndex, from + CHUNK_FETCH_BATCH - 1);
          const resp: any = await getApiCall(
            API_SESSION_REPLAY_CHUNKS(sessionId, from, to),
            {},
          );
          if (!aliveRef.current) return;
          const chunks = Array.isArray(resp?.chunks) ? resp.chunks : [];
          for (const c of chunks) {
            rrwebEventsRef.current.push(...decodeChunk(c));
            loadedCount += 1;
          }
          rrwebEventsRef.current.sort((a: any, b: any) => a.timestamp - b.timestamp);
          setBufferedFraction(Math.min(1, loadedCount / meta.length));

          // Reveal the player as soon as we have a renderable set; the
          // Replayer itself is built by an effect once the canvas has mounted.
          if (rrwebEventsRef.current.length >= 2) setLoading(false);
        }

        if (rrwebEventsRef.current.length < 2) {
          setLoadError('Replay data could not be decoded for this session.');
          setLoading(false);
          return;
        }

        setLoading(false);
        setAllLoaded(true);
      } catch (e: any) {
        if (!aliveRef.current) return;
        setLoadError(friendlyApiError(e) || 'Failed to load the session replay.');
        setLoading(false);
        toast.error(friendlyApiError(e) || 'Failed to load session replay');
      }
    })();

    return () => {
      aliveRef.current = false;
      destroyReplayer();
      rrwebEventsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (loading || loadError) return;
    if (!canvasRef.current) return;
    if (rrwebEventsRef.current.length < 2) return;
    const at = replayerRef.current ? replayerRef.current.getCurrentTime() : 0;
    const wasPlaying = playingRef.current;
    buildReplayer(at, wasPlaying);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, allLoaded, loadError]);

  // ---- playback position polling ----
  useEffect(() => {
    const timer = setInterval(() => {
      const replayer = replayerRef.current;
      if (replayer && playingRef.current) {
        try {
          setCurrentMs(replayer.getCurrentTime());
        } catch {
          /* ignore */
        }
      }
    }, 200);
    return () => clearInterval(timer);
  }, []);

  // ---- controls ----
  const handleSeek = useCallback(
    (offsetMs: number) => {
      const replayer = replayerRef.current;
      if (!replayer) return;
      const clamped = Math.max(0, Math.min(offsetMs, durationMs * bufferedFraction));
      try {
        if (playingRef.current) replayer.play(clamped);
        else replayer.pause(clamped);
        setCurrentMs(clamped);
      } catch {
        /* ignore */
      }
    },
    [durationMs, bufferedFraction],
  );

  const handlePlayPause = useCallback(() => {
    const replayer = replayerRef.current;
    if (!replayer) return;
    try {
      if (playingRef.current) {
        replayer.pause();
        setPlaying(false);
      } else {
        replayer.play(replayer.getCurrentTime());
        setPlaying(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleSpeedChange = useCallback((s: number) => {
    setSpeed(s);
    try {
      replayerRef.current?.setConfig({ speed: s });
    } catch {
      /* ignore */
    }
  }, []);

  const handleSkipIdleToggle = useCallback(() => {
    setSkipIdle((prev) => {
      const next = !prev;
      try {
        replayerRef.current?.setConfig({ skipInactive: next });
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const handleJump = useCallback(
    (deltaMs: number) => {
      const replayer = replayerRef.current;
      if (!replayer) return;
      handleSeek(replayer.getCurrentTime() + deltaMs);
    },
    [handleSeek],
  );

  // space = play/pause
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.code === 'Space' &&
        !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)
      ) {
        e.preventDefault();
        handlePlayPause();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlePlayPause]);

  useEffect(() => {
    window.addEventListener('resize', applyScale);
    return () => window.removeEventListener('resize', applyScale);
  }, [applyScale]);

  const tabBtn = (key: string, label: string) => (
    <button
      onClick={() => setRightTab(key)}
      style={{
        flex: 1,
        padding: '8px 0',
        border: 'none',
        borderBottom:
          rightTab === key ? '2px solid #4f46e5' : '2px solid transparent',
        background: 'transparent',
        fontWeight: rightTab === key ? 600 : 400,
        // color: rightTab === key ? "#111827" : "#6b7280",
        cursor: 'pointer',
        fontSize: 13,
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      className="m-3 flex flex-col rounded-2xl border border-border/60 bg-card p-4 sr-block"
      style={{
        minHeight: 'calc(100vh - 140px)',
        boxShadow:
          '10px 10px 24px rgba(163, 177, 198, 0.4), -10px -10px 24px rgba(255, 255, 255, 0.9)',
      }}
    >
        <div className="mb-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border/60 bg-card text-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.35),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
            aria-label="Back"
            title="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Film className="h-5 w-5" />
          </span>
          <div>
            <h4 className="m-0 text-lg font-bold text-foreground">Session Replay</h4>
            <p className="m-0 text-xs text-muted-foreground">
              Playback + timeline + heatmap for the selected agent session.
            </p>
          </div>
        </div>

        <SessionSummaryHeader session={session} />

        {loading ? (
          <Loader />
        ) : loadError ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-6 py-12 text-center dark:border-rose-900/40 dark:bg-rose-950/30">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-600 ring-1 ring-inset ring-rose-200 dark:bg-rose-950/50 dark:text-rose-300">
              <AlertTriangle className="h-6 w-6" />
            </span>
            <div className="text-sm font-semibold text-rose-700 dark:text-rose-300">
              {loadError}
            </div>
          </div>
        ) : (
          <>
            <TimelineScrubber
              durationMs={durationMs}
              currentMs={currentMs}
              bufferedFraction={bufferedFraction}
              events={events}
              idleRanges={idleRanges}
              sessionStartTs={session?.started_at}
              onSeek={handleSeek}
            />

            <div style={{ display: 'flex', gap: 12, flex: 1, minHeight: 420 }}>
              {/* replay canvas + heatmap overlay */}
              <div
                className="relative flex-1 overflow-hidden rounded-xl border border-border/60 bg-muted/30"
                style={{
                  minWidth: 0,
                  boxShadow:
                    'inset 3px 3px 8px rgba(163, 177, 198, 0.3), inset -3px -3px 8px rgba(255, 255, 255, 0.7)',
                }}
              >
                <div ref={canvasRef} style={{ position: 'absolute', inset: 0 }} />
                {heatmapOn && (
                  <HeatmapOverlay
                    cells={heatmap.cells}
                    maxClicks={heatmap.max}
                    screen={heatmap.screen}
                  />
                )}
              </div>
              {/* right panel */}
              {/* <div
                style={{
                  width: 340,
                  flex: "0 0 340px",
                  border: "1px solid #e5e7eb68",
                  borderRadius: 10,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  // background: "#fff",
                }}
              >
                <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb" }}>
                  {tabBtn("timeline", "Timeline")}
                  {tabBtn("journey", "Journey")}
                  {tabBtn("insights", "Insights")}
                </div>
                <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {rightTab === "timeline" ? (
                    <SemanticTimeline
                      events={events}
                      sessionStartTs={session?.started_at}
                      currentMs={currentMs}
                      onSeek={handleSeek}
                    />
                  ) : rightTab === "journey" ? (
                    <JourneyView
                      journey={journey}
                      sessionStartTs={session?.started_at}
                      onSeek={handleSeek}
                    />
                  ) : (
                    <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                      {sessionInsights.length ? (
                        sessionInsights.map((ins, i) => (
                          <div
                            key={i}
                            style={{
                              // background: ins.type === "ux_issue" ? "#fef2f2" : "#f8fafc",
                              border: `1px solid ${ins.type === "ux_issue" ? "#fecaca5a" : "#e2e8f068"}`,
                              borderLeft: `3px solid ${markerColor(
                                ins.type === "ux_issue" ? "rage_click" : "screen_change"
                              )}`,
                              borderRadius: 8,
                              padding: "10px 12px",
                              fontSize: 13,
                              // color: "#1f2937",
                            }}
                          >
                            {ins.text}
                          </div>
                        ))
                      ) : (
                        <div style={{ textAlign: "center", padding: 20 }}>
                          Not enough activity for insights.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div> */}
            </div>

            <PlaybackControls
              playing={playing}
              speed={speed}
              skipIdle={skipIdle}
              heatmapOn={heatmapOn}
              currentMs={currentMs}
              durationMs={durationMs}
              onPlayPause={handlePlayPause}
              onSpeedChange={handleSpeedChange}
              onSkipIdleToggle={handleSkipIdleToggle}
              onHeatmapToggle={() => setHeatmapOn((v) => !v)}
              onJump={handleJump}
            />
          </>
        )}
    </div>
  );
}

export default SessionReplayPlayer;
